import { useEffect, useRef, useState } from 'react';
import useCanvasStore from '../stores/canvasStore';

/**
 * Helper function to serialize canvas elements without circular references
 * Removes pixiSprite property which contains circular references
 */
const serializeElements = (elements) => {
  if (!elements) return null;

  return elements.map(element => {
    const { pixiSprite, captionSprite, ...rest } = element;
    return rest;
  });
};

/**
 * Helper function to check if user is actively interacting with elements
 * Checks for active mouse buttons or ongoing transformations
 */
const isUserInteracting = () => {
  // Check if any mouse buttons are pressed
  if (document.querySelector('[data-transforming="true"]')) {
    return true;
  }

  // Check for active pointer events (dragging, resizing, etc.)
  const activePointers = document.querySelectorAll(':active');
  if (activePointers.length > 0) {
    // Filter out non-canvas interactions (like clicking buttons)
    for (let element of activePointers) {
      if (element.closest('canvas') || element.tagName === 'CANVAS') {
        return true;
      }
    }
  }

  return false;
};

/**
 * Hook to handle autosaving when canvasElements change
 * @param {Array} canvasElements - The canvas elements to monitor
 * @param {Function} saveFunction - The function to call when autosaving
 * @param {number} delay - Debounce delay in milliseconds (default: 2000)
 * @returns {Object} - { isSaving, lastSaved, saveStatus }
 */
export const useAutosave = (canvasElements, saveFunction, delay = 2000) => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const isInitialMount = useRef(true);
  const previousElementsRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const pendingSaveRef = useRef(false);
  const isInteractingRef = useRef(false);

  // Get project loading state from store
  const isProjectLoading = useCanvasStore((state) => state.isProjectLoading);
  const expectedLayerCount = useCanvasStore((state) => state.expectedLayerCount);
  const projectLoadFailed = useCanvasStore((state) => state.projectLoadFailed);

  // Track mouse interactions
  useEffect(() => {
    const handleMouseDown = () => {
      isInteractingRef.current = true;
    };

    const handleMouseUp = () => {
      isInteractingRef.current = false;

      // If there's a pending save, trigger it after interaction ends
      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        // Trigger save after a short delay to ensure interaction is complete
        setTimeout(() => {
          if (!isInteractingRef.current) {
            performSave();
          }
        }, 500);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const performSave = async () => {
    // Don't save if project is still loading
    if (isProjectLoading) {
      pendingSaveRef.current = true;
      return;
    }

    // Don't save if project load had failures - prevents overwriting good layers with incomplete state
    if (projectLoadFailed) {
      console.warn('🛡️ Autosave blocked - project load had failures, preventing layer data loss');
      return;
    }

    // Don't save if we have fewer layers than expected (layers may have failed to load)
    // This prevents overwriting the database with an incomplete layer set
    if (expectedLayerCount > 0 && canvasElements && canvasElements.length < expectedLayerCount) {
      console.warn(`🛡️ Autosave blocked - canvas has ${canvasElements.length} layers but project expects ${expectedLayerCount}. Preventing potential layer data loss.`);
      return;
    }

    // Don't save if user is still interacting
    if (isInteractingRef.current) {
      pendingSaveRef.current = true;
      return;
    }

    setIsSaving(true);
    setSaveStatus('saving');

    try {
      await saveFunction();
      setLastSaved(new Date());
      setSaveStatus('saved');
      const serializedElements = serializeElements(canvasElements);
      previousElementsRef.current = JSON.stringify(serializedElements);
      pendingSaveRef.current = false;

      // Reset status to idle after 3 seconds
      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    } catch (error) {
      console.error('Autosave failed:', error);
      setSaveStatus('error');

      // Reset error status after 5 seconds
      setTimeout(() => {
        setSaveStatus('idle');
      }, 5000);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousElementsRef.current = JSON.stringify(serializeElements(canvasElements));
      return;
    }

    // Only autosave if there are elements
    if (!canvasElements || canvasElements.length === 0) {
      return;
    }

    // Don't autosave if project load had failures
    if (projectLoadFailed) {
      return;
    }

    // Don't autosave if we have fewer layers than expected
    if (expectedLayerCount > 0 && canvasElements.length < expectedLayerCount) {
      return;
    }

    // Serialize elements without pixiSprite to avoid circular references
    const serializedElements = serializeElements(canvasElements);
    const currentElements = JSON.stringify(serializedElements);

    // Check if elements actually changed
    if (currentElements === previousElementsRef.current) {
      return;
    }

    // Debounce the save operation
    debounceTimerRef.current = setTimeout(() => {
      // Check if project is still loading
      if (isProjectLoading) {
        pendingSaveRef.current = true;
        return;
      }

      // Check if user is actively interacting before saving
      if (isInteractingRef.current) {
        console.log('⏸️ Autosave paused - user is interacting with elements');
        pendingSaveRef.current = true;
        return;
      }

      performSave();
    }, delay);

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [canvasElements, saveFunction, delay]);

  return {
    isSaving,
    lastSaved,
    saveStatus
  };
};